import React from 'react';
import { AlertCircle, RefreshCw } from 'lucide-react';

/**
 * Catches a render error in the page below it and shows a short explanation
 * instead of a blank screen. Without it, one bad value (an object where text was
 * expected, say) unmounted the whole portal, sidebar included.
 *
 * Layouts give it `key={location.pathname}`, so moving to another page clears
 * the error.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    console.error('[ErrorBoundary]', error, info?.componentStack);
  }

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div role="alert" className="max-w-xl mx-auto my-12 rounded-xl border border-red-200 bg-red-50 p-6 text-sm text-red-900">
        <p className="flex items-center gap-2 font-semibold text-base">
          <AlertCircle className="w-5 h-5 shrink-0" aria-hidden="true" />
          This page stopped working
        </p>
        <p className="mt-2">
          Something on the page could not be shown. Your work elsewhere in the Sandbox is not
          affected. Reload the page, or use the menu to go to another page.
        </p>
        <p className="mt-2 font-mono text-xs text-red-800 break-words">
          {String(this.state.error?.message || this.state.error)}
        </p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="mt-4 inline-flex items-center gap-2 rounded-lg bg-red-700 px-3 py-1.5 font-medium text-white hover:bg-red-800"
        >
          <RefreshCw className="w-4 h-4" aria-hidden="true" /> Reload the page
        </button>
      </div>
    );
  }
}
